/* Name matching for the member picker.

   Pure and separate from the component so the ordering rules can be
   tested directly — they are the whole point of the control, and they
   are easy to get subtly wrong with Icelandic names. */

const FOLD: Record<string, string> = {
  á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u', ý: 'y', þ: 'th', æ: 'ae', ö: 'o', ð: 'd',
};

/** Lowercase, and strip Icelandic letters to their nearest ASCII, so a
    name typed without accents still matches. */
export const fold = (s: string): string =>
  s.toLowerCase().replace(/[áéíóúýþæöð]/g, (c) => FOLD[c] ?? c);

export type PickableMember = { id: number; name: string; attendance?: number };

/**
 * Alphabetical, Icelandic. Not SQLite's job: its NOCASE collation only
 * folds ASCII, so Á, Ð, Þ, Æ and Ö would all sort after Z. Icelandic
 * puts them in their own places — a, á, b, d, ð, e … y, ý, þ, æ, ö.
 */
export const byName = (a: { name: string }, b: { name: string }): number =>
  a.name.localeCompare(b.name, 'is');

/**
 * Everyone not already chosen who matches `query`, alphabetically —
 * except that names *beginning* with the query float above names that
 * merely contain it, so typing "ar" offers Ármann before Óskarsson.
 */
export function matchMembers<T extends PickableMember>(
  members: T[],
  selected: number[],
  query: string,
): T[] {
  const q = fold(query.trim());
  return members
    .filter((m) => !selected.includes(m.id))
    .filter((m) => !q || fold(m.name).includes(q))
    .sort((a, b) => {
      if (q) {
        const ap = fold(a.name).startsWith(q) ? 0 : 1;
        const bp = fold(b.name).startsWith(q) ? 0 : 1;
        if (ap !== bp) return ap - bp;
      }
      return byName(a, b);
    });
}

/** The chosen members, alphabetical — chips should not jump around in
    the order they happened to be clicked. */
export function chosenMembers<T extends PickableMember>(members: T[], selected: number[]): T[] {
  return selected
    .map((id) => members.find((m) => m.id === id))
    .filter((m): m is T => !!m)
    .sort(byName);
}
